<template>
  <div>
    <navbar :links="links" />
    <p class="title mb-2 mt-4">
      {{ $t('workspacePageHeader') }}
    </p>
    <!-- Loading indicator -->
    <v-layout
      v-if="loading"
      justify-center
    >
      <v-progress-circular
        indeterminate
        style="position: fixed; z-index: 5"
        class="mt-5 msr--text"
        :size="50"
      />
    </v-layout>
    <div class="headline grey--text text--darken-3 mb-4">
      {{ workspacePage.name }}
    </div>
    <v-divider class="mb-3" />
    <table class="subheading">
      <!-- Workspace Manager -->
      <tr v-if="workspacePage.managed_by">
        <td class="attribute">
          {{ $t('workspaceManager') }}:
        </td>
        <td>
          {{ workspacePage.managed_by.first_name }}
          {{ workspacePage.managed_by.last_name }}
          (<a :href="'mailto:' + workspacePage.managed_by.email">{{
            workspacePage.managed_by.email
          }}</a>)
        </td>
      </tr>
      <!-- Email Domains -->
      <tr v-if="workspacePage.email_domains">
        <td class="attribute">
          {{ $t('workspacePageDomains') }}:
        </td>
        <td>
          <v-layout wrap>
            <div
              v-for="(domain, domainIndex) in workspacePage.email_domains"
              :key="domain + domainIndex"
            >
              {{ domain
              }}<span
                v-if="domainIndex < workspacePage.email_domains.length - 1"
              >,&nbsp;</span>
            </div>
          </v-layout>
        </td>
      </tr>
      <!-- Number of Users -->
      <tr v-if="workspacePage.num_users">
        <td class="attribute">
          {{ $t('workspacePageNumUsers') }}:
        </td>
        <td>
          {{ workspacePage.num_users }}
          <span class="ml-2 nowrap">
            <v-icon color="msr">shortcut</v-icon>
            <router-link :to="{name: 'userManagementManage'}">
              {{ $t('userManagementManage') }}
            </router-link>
          </span>
        </td>
      </tr>
    </table>
    <!-- Used Products/Services -->
    <div class="subheading mt-4">
      <div
        class="mb-3"
        style="text-decoration: underline"
      >
        {{ $t('workspacePageUsedServices') }}:
      </div>
      <!-- Number of Radio Stores/Players -->
      <v-layout
        class="mt-2"
        align-center
      >
        <v-icon
          v-if="workspacePage.num_radio_decoders > 0"
          color="success"
          class="mr-2"
        >
          check
        </v-icon>
        <v-icon
          v-else
          color="error"
          class="mr-2"
        >
          clear
        </v-icon>
        <span class="mr-1 font-weight-bold nowrap">INSTORE-RADIO</span>
        <span v-if="workspacePage.num_radio_decoders">
          ({{ workspacePage.num_radio_stores }} {{ $t('sites') }},
          {{ workspacePage.num_radio_decoders }} {{ $t('decoders') }})
          <span class="ml-2 nowrap">
            <v-icon color="msr">shortcut</v-icon>
            <router-link :to="{name: 'devices'}">{{
              $t('devices')
            }}</router-link>
          </span>
        </span>
      </v-layout>
      <!-- Number of TV Stores/Players -->
      <v-layout
        class="mt-2"
        align-center
      >
        <v-icon
          v-if="workspacePage.num_tv_decoders > 0"
          color="success"
          class="mr-2"
        >
          check
        </v-icon>
        <v-icon
          v-else
          color="error"
          class="mr-2"
        >
          clear
        </v-icon>
        <span class="mr-1 font-weight-bold nowrap">INSTORE-TV</span>
        <span v-if="workspacePage.num_tv_decoders">
          ({{ workspacePage.num_tv_stores }} {{ $t('sites') }},
          {{ workspacePage.num_tv_decoders }} {{ $t('decoders') }})
          <span class="ml-2 nowrap">
            <v-icon color="msr">shortcut</v-icon>
            <router-link :to="{name: 'devices'}">{{
              $t('devices')
            }}</router-link>
          </span>
        </span>
      </v-layout>
    </div>
    <!-- Add-Ons -->
    <div class="subheading mt-4">
      <div
        class="mb-3"
        style="text-decoration: underline"
      >
        {{ $t('workspacePagePaidAddons') }}:
      </div>
      <!-- Visitorcount -->
      <v-layout
        class="mt-2"
        align-center
      >
        <v-icon
          v-if="workspacePage.uses_addon_visitorcount"
          color="success"
          class="mr-2"
        >
          check
        </v-icon>
        <v-icon
          v-else
          color="error"
          class="mr-2"
        >
          clear
        </v-icon>
        <span class="mr-1 font-weight-bold">{{
          $t('visitorcount').toUpperCase()
        }}</span>
        <span class="ml-2 nowrap">
          <v-icon color="msr">shortcut</v-icon>
          <span
            class="pseudo-anchor msr--text"
            @click="goToVisitorcountPage()"
          >{{ $t('visitorcount') }}</span>
        </span>
      </v-layout>
      <!-- Content Creator -->
      <v-layout
        v-if="
          $store.getters.userManagesContentForAtLeastOneGroup &&
            ($store.getters.showContentCreatorLandingPageItem ||
              $store.getters.showAllContentCreatorItems)
        "
        class="mt-2"
        align-center
      >
        <v-icon
          v-if="workspacePage.uses_addon_contentcreator"
          color="success"
          class="mr-2"
        >
          check
        </v-icon>
        <v-icon
          v-else
          color="error"
          class="mr-2"
        >
          clear
        </v-icon>
        <span class="mr-1 font-weight-bold nowrap">{{
          $t('contentcreator').toUpperCase()
        }}</span>
        <span class="ml-2 nowrap">
          <v-icon color="msr">shortcut</v-icon>
          <router-link
            v-if="$userprofile && $userprofile.content_creator_plan_booking"
            :to="{name: 'contentcreatorbilling'}"
          >
            {{ $t('contentCreatorGoToPlansPage') }}
          </router-link>
          <router-link
            v-else
            :to="{name: 'contentcreatorlanding'}"
          >
            {{ $t('contentCreatorGoToLandingPage') }}
          </router-link>
        </span>
      </v-layout>
      <!-- Menus -->
      <v-layout
        class="mt-2"
        align-center
      >
        <v-icon
          v-if="workspacePage.uses_addon_menus"
          color="success"
          class="mr-2"
        >
          check
        </v-icon>
        <v-icon
          v-else
          color="error"
          class="mr-2"
        >
          clear
        </v-icon>
        <span class="mr-1 font-weight-bold">{{
          $t('menus').toUpperCase()
        }}</span>
        <span class="ml-2 nowrap">
          <v-icon color="msr">shortcut</v-icon>
          <router-link :to="{name: 'menus'}">
            {{ $t('menus') }}
          </router-link>
        </span>
      </v-layout>
    </div>
    <!-- Payment Info -->
    <div
      v-if="showPaymentInfo"
      class="subheading mt-4"
    >
      <div
        class="mb-3"
        style="text-decoration: underline"
      >
        {{ $t('workspacePagePaymentInfo') }}:
      </div>
      <!-- SEPA Form Link -->
      <v-layout
        class="mt-2"
        align-center
      >
        <span class="ml-1">
          <v-icon color="msr">shortcut</v-icon>
          <span
            class="pseudo-anchor msr--text"
            @click="openSepaRedirectDialog()"
          >{{ $t('workspacePageSepaDirectDebitMandateLink') }}</span>
        </span>
      </v-layout>
    </div>
  </div>
</template>

<script>
import {redirectToExternalUrl} from '@/utils.js';

import Navbar from '@/components/Navbar';

export default {
  components: {
    Navbar,
  },
  data() {
    return {
      links: ['dashboard', 'workspace'],
      loading: false,
      workspacePage: {},
    };
  },
  computed: {
    showPaymentInfo() {
      return (
        this.$user &&
        !!this.$userprofile.workspace &&
        this.$userprofile.workspace.ask_for_sepa_direct_debit_mandate &&
        !!this.$userprofile.workspace.sepa_direct_debit_mandate_form_url
      );
    },
  },
  created() {
    this.fetchWorkspacePage();
  },
  methods: {
    fetchWorkspacePage() {
      this.loading = true;
      this.$api
        .retrieveUserWorkspacePage()
        .then((response) => {
          this.workspacePage = response.data;
        })
        .finally(() => (this.loading = false));
    },
    goToVisitorcountPage() {
      redirectToExternalUrl(this, '/client/visitorcount');
    },
    openSepaRedirectDialog() {
      this.$bus.$emit('show-ask-for-sepa-direct-debit-mandate-dialog');
    },
  },
};
</script>

<style scoped>
table td {
  padding-top: 4px;
  padding-bottom: 4px;
}
table td.attribute {
  text-align: right;
  padding-right: 8px;
  vertical-align: baseline;
  font-weight: bold;
}
@media (min-width: 599px) {
  table td.attribute {
    white-space: nowrap;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/Workspace.vue